import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useOnboarding } from './OnboardingContext';
import './TourTooltip.css';

const TourTooltip = () => {
    const { isActive, currentStep, skipTour, nextStep } = useOnboarding();
    const [style, setStyle] = useState({});

    // Define our magical steps!
    const steps = [
        { targetId: 'btn-create-new-project', text: "Welcome to Alekhine AI! Let's direct your first movie. Click here to begin.", pos: 'right', alignTop: true },
        { targetId: 'new-project-idea-input', text: "We'll start with a classic concept...", pos: 'bottom', autoType: true },
        { targetId: 'btn-submit-new-project', text: "Click here to generate a full story from this idea.", pos: 'top' },
        { targetId: 'new-project-idea-input', text: "Here is your AI-generated story! Take a read.", pos: 'bottom' },
        { targetId: 'btn-submit-new-project', text: "Now, click create to initialize your project!", pos: 'top' },
        { targetId: 'tour-generate-screenplay', text: "Let's write the script.", pos: 'right' },
        { targetId: 'tour-generate-characterBible', text: "Now let's visualize your cast.", pos: 'right' },
        { targetId: 'tour-generate-scenes', text: "Time to generate the shot descriptions and storyboards.", pos: 'right' },
        { targetId: 'tour-generate-videos', text: "Let's generate the video clips.", pos: 'right' },
        { targetId: 'tour-merge', text: "Finally, let's bring it all together into a movie!", pos: 'right', isLast: true },
    ];

    // Auto-advance Step 3 (index 3) after 1.5 seconds
    useEffect(() => {
        if (isActive && currentStep === 3) {
            const timer = setTimeout(() => {
                nextStep();
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [isActive, currentStep, nextStep]);

    useEffect(() => {
        if (!isActive || currentStep >= steps.length) return;

        const updatePosition = () => {
            const step = steps[currentStep];
            const el = document.getElementById(step.targetId);

            if (el) {
                const rect = el.getBoundingClientRect();
                const offset = (window.innerHeight / 100) * 1.5; // 1.5vh equivalent offset
                let top = 0, left = 0;

                // Position logic
                if (step.pos === 'right') {
                    if (step.alignTop) {
                        top = rect.top; // Align top of tooltip near top of element
                    } else {
                        top = rect.top + (rect.height / 2); // Center align
                    }
                    left = rect.right + offset;
                } else if (step.pos === 'bottom') {
                    top = rect.bottom + offset;
                    left = rect.left + (rect.width / 2);
                } else if (step.pos === 'top') {
                    top = rect.top - offset;
                    left = rect.left + (rect.width / 2);
                } else if (step.pos === 'left') {
                    top = rect.top + (rect.height / 2);
                    left = rect.left - offset;
                }

                setStyle(prev => {
                    if (prev.top === top && prev.left === left && prev.opacity === 1) return prev;
                    return { top, left, opacity: 1 };
                });

                // Ensure the element flashes to draw attention
                el.classList.add('tour-active-element');
            } else {
                // Element not found on DOM yet (e.g. modal opening), hide tooltip temporarily
                setStyle(prev => {
                    if (prev.opacity === 0) return prev;
                    return { opacity: 0 };
                });
            }
        };

        // Use interval to catch DOM changes (like modal opening) since ResizeObserver is complex here
        updatePosition();
        const interval = setInterval(updatePosition, 250);
        window.addEventListener('resize', updatePosition);

        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', updatePosition);
            // Clean up glow on unmount/step change
            document.querySelectorAll('.tour-active-element').forEach(el => el.classList.remove('tour-active-element'));
        };
    }, [isActive, currentStep]);

    if (!isActive || currentStep >= steps.length) return null;

    const step = steps[currentStep];

    return ReactDOM.createPortal(
        <div className={`tour-tooltip pos-${step.pos} ${style.opacity ? 'active' : ''} ${step.alignTop ? 'align-top' : ''}`} style={style}>
            <div className="tour-tooltip-content">
                <p>{step.text}</p>
            </div>
            <div className="tour-tooltip-footer">
                <button className="tour-tooltip-skip" onClick={skipTour}>Skip Tour</button>
                {step.showNext && (
                    <button className="tour-tooltip-next" onClick={nextStep}>Next</button>
                )}
                {step.isLast && (
                    <button className="tour-tooltip-next success" onClick={skipTour}>Got it!</button>
                )}
            </div>
        </div>,
        document.body
    );
};

export default TourTooltip;
